import { ReactNode } from "react";
import classnames from "classnames";
import { useFormContext, useWatch } from "react-hook-form";
import { Info } from "../Icons/general-interface-1";

import styles from "./index.module.scss";

type PropsType = {
  section?: string; // section.id
  id: string;
  label?: string;
  description?: string;
  comment?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  maxLength?: number;
  placeholder?: string;
  requiredText?: string;
  className?: string;
  tooltip?: ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fieldOptions?: any;
};

const TextArea = ({
  section,
  id,
  label,
  description,
  placeholder,
  maxLength = 20,
  disabled,
  readOnly,
  comment,
  requiredText,
  className,
  tooltip,
  fieldOptions,
}: PropsType) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const idDotFormat = (() => {
    if (comment && section) {
      return `comments.${section}.${id}.temporary-text`;
    }
    if (comment) {
      return `comments.${id}`;
    }
    if (section) {
      return `${section}.${id}`;
    }
    return `${id}`;
  })();
  const values = useWatch({ name: idDotFormat });
  const countChar = values ? values.toString().length : 0;
  const noText = countChar === 0;
  const remainingChar = `${countChar} / ${maxLength} characters`;
  const error = (() => {
    if (comment && section && errors.comments && errors.comments[section]) {
      return errors.comments[section][id];
    }
    if (section && errors[section]) {
      return errors[section][id];
    }
    return errors[id];
  })();

  const customRegister = register(idDotFormat, {
    maxLength: {
      value: maxLength,
      message: `Cannot exceed ${maxLength} characters`,
    },
    ...fieldOptions,
    ...(requiredText ? { required: requiredText } : {}),
  });

  return (
    <div className={className}>
      {label && (
        <label htmlFor={idDotFormat} className={styles.labelText}>
          {label} {tooltip}
        </label>
      )}
      {description && <p className={styles.descriptionText}>{description}</p>}
      <div className={styles.textInputContainer}>
        <textarea
          className={classnames(styles.textInput, {
            [styles.textInputFixed]: comment,
            [styles.textInputError]: error,
            [styles.textInputFilled]: !noText,
          })}
          aria-describedby={`${idDotFormat} Text Area`}
          placeholder={placeholder}
          id={idDotFormat}
          disabled={disabled}
          readOnly={readOnly}
          {...customRegister}
        />
        <p
          className={classnames(styles.remainingText, {
            [styles.remainingTextError]: error,
          })}
        >
          {error && <Info className={styles.icon} />}
          {remainingChar} {error && error.message}
        </p>
      </div>
    </div>
  );
};

export default TextArea;
